/**
 * Aplica sugestões de otimização de fluxo retornadas pela IA.
 */

import { acoesFromSlot, acoesToPayload, legacyFieldsFromAcoes } from "./chatbotSlotActions";
import { buildSlotPath, slotStepName } from "./chatbotNavigationActions";

export const SUGGESTION_STATUS_PENDING = "pending";
export const SUGGESTION_STATUS_APPLIED = "applied";
export const SUGGESTION_STATUS_DISCARDED = "discarded";

export function enrichSuggestions(suggestions, slots = []) {
  const slotById = new Map((slots || []).map((s) => [Number(s.id), s]));
  return (suggestions || []).map((s) => {
    const slot = slotById.get(Number(s.slotId));
    return {
      ...s,
      status: SUGGESTION_STATUS_PENDING,
      slotPath: s.slotPath || (slot ? buildSlotPath(slot.id, slotById) : ""),
      slotLabel: s.slotLabel || (slot ? slotStepName(slot) : `#${s.slotId}`),
    };
  });
}

function mergeGotoAction(slot, gotoSlotId) {
  const existing = acoesFromSlot(slot);
  const targetId = Number(gotoSlotId);
  const already = existing.some((a) => Number(a.tipo_acao) === 2 && Number(a.id_acao) === targetId);
  if (already) return null;

  const acoes = [
    ...existing,
    {
      tipo_acao: 2,
      id_acao: targetId,
      values: {},
      labels: {},
    },
  ];
  return {
    acoes: acoesToPayload(acoes),
    ...legacyFieldsFromAcoes(acoes),
  };
}

/**
 * Monta payload PUT /chatbot/slots/:id a partir de uma sugestão.
 * @returns {object|null}
 */
export function buildUpdatePayloadFromSuggestion(suggestion, slots = []) {
  if (!suggestion?.changes) return null;
  const slotId = Number(suggestion.slotId);
  const slot = (slots || []).find((s) => Number(s.id) === slotId);
  if (!slot) return null;

  const payload = { id: slotId };
  const { changes, type } = suggestion;

  if (changes.resposta != null) payload.resposta = String(changes.resposta);
  if (changes.nome != null) payload.nome = String(changes.nome);
  if (changes.identificador != null) payload.identificador = String(changes.identificador);
  if (changes.botoes != null) payload.botoes = changes.botoes;

  if (type === "reuse_slot" && changes.gotoSlotId) {
    const gotoPatch = mergeGotoAction(slot, changes.gotoSlotId);
    if (!gotoPatch) return null;
    Object.assign(payload, gotoPatch);
  }

  return payload;
}

export function describeSuggestionChange(suggestion, slots = []) {
  const slot = (slots || []).find((s) => Number(s.id) === Number(suggestion?.slotId));
  const changes = suggestion?.changes || {};
  const parts = [];

  if (changes.resposta != null) {
    parts.push({
      field: "resposta",
      before: String(slot?.resposta || "").trim(),
      after: String(changes.resposta).trim(),
    });
  }
  if (changes.botoes != null) {
    const before = Array.isArray(slot?.botoes)
      ? slot.botoes.join(", ")
      : String(slot?.botoes || "");
    parts.push({
      field: "botoes",
      before,
      after: changes.botoes.join(", "),
    });
  }
  if (changes.identificador != null) {
    parts.push({
      field: "identificador",
      before: String(slot?.identificador || ""),
      after: String(changes.identificador),
    });
  }
  if (changes.nome != null) {
    parts.push({
      field: "nome",
      before: String(slot?.nome || ""),
      after: String(changes.nome),
    });
  }
  if (changes.gotoSlotId != null) {
    const target = (slots || []).find((s) => Number(s.id) === Number(changes.gotoSlotId));
    parts.push({
      field: "goto",
      before: "—",
      after: target ? slotStepName(target) : `#${changes.gotoSlotId}`,
    });
  }

  return parts;
}

export async function applyOptimizationSuggestion(suggestion, { updateSlot, slots }) {
  const payload = buildUpdatePayloadFromSuggestion(suggestion, slots);
  if (!payload) {
    throw new Error("suggestion_not_applicable");
  }
  await updateSlot.mutateAsync(payload);
  return payload;
}

export async function applyAllOptimizationSuggestions(suggestions, deps) {
  const applied = [];
  const failed = [];
  for (const suggestion of suggestions) {
    if (suggestion.status !== SUGGESTION_STATUS_PENDING) continue;
    try {
      await applyOptimizationSuggestion(suggestion, deps);
      applied.push(suggestion.id);
    } catch (err) {
      failed.push({ id: suggestion.id, message: err?.message || String(err) });
    }
  }
  return { applied, failed };
}
